import React, { useState } from "react"
import { manageProfilePassword } from "../../../api/manage_profile_password"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../LoadingContext"

import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import Visibility from "@mui/icons-material/Visibility"
import VisibilityOff from "@mui/icons-material/VisibilityOff"

interface ManagePasswordProps {
  onClose: (num: number) => void // モーダルを閉じるためのコールバック
}

const ManagePassword: React.FC<ManagePasswordProps> = ({ onClose }) => {
  const { startLoading, stopLoading } = useLoading()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    password: "",
    newPassword: "",
    newPasswordCheck: ""
  })

  const [errors, setErrors] = useState({
    password: "",
    newPassword: "",
    newPasswordCheck: ""
  })

  // インデックスシグネチャを使って型を定義
  const [showPassword, setShowPassword] = useState<{
    password: boolean
    newPassword: boolean
    newPasswordCheck: boolean
  }>({
    password: false,
    newPassword: false,
    newPasswordCheck: false
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement // 明示的に型キャスト
    const { name, value, type, checked } = target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }))
  }

  const handleTogglePasswordVisibility = (field: keyof typeof showPassword) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field]
    }))
  }

  const validate = () => {
    const validationErrors = { ...errors }
    let hasError = false

    const allowedSymbols = "!#$%&()-^[];:,.~|`{+*}?_"
    const invalidCharRegex = new RegExp(`[^a-zA-Z0-9${allowedSymbols.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&")}]`)
    const uppercaseRegex = /[A-Z]/
    const lowercaseRegex = /[a-z]/
    const numberRegex = /[0-9]/
    const symbolRegex = new RegExp(`[${allowedSymbols.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&")}]`)

    // 現在のパスワードのバリデーション
    if (!formData.password) {
      validationErrors.password = "必須項目が入力されていません"
      hasError = true
    } else {
      validationErrors.password = ""
    }

    // 新しいパスワードのバリデーション
    if (!formData.newPassword) {
      validationErrors.newPassword = "必須項目が入力されていません"
      hasError = true
    } else if (formData.newPassword.length < 12 || formData.newPassword.length > 32) {
      validationErrors.newPassword = "入力文字数は12文字から32文字以内です"
      hasError = true
    } else if (invalidCharRegex.test(formData.newPassword)) {
      validationErrors.newPassword = "使用できない文字が含まれています"
      hasError = true
    } else if (
      !uppercaseRegex.test(formData.newPassword) ||
      !lowercaseRegex.test(formData.newPassword) ||
      !numberRegex.test(formData.newPassword) ||
      !symbolRegex.test(formData.newPassword)
    ) {
      validationErrors.newPassword = "英大文字・英小文字・数字・記号をそれぞれ1文字以上含めてください"
      hasError = true
    } else {
      validationErrors.newPassword = ""
    }

    // 確認用パスワードのバリデーション
    if (!formData.newPasswordCheck) {
      validationErrors.newPasswordCheck = "必須項目が入力されていません"
      hasError = true
    } else if (formData.newPasswordCheck.length < 12 || formData.newPasswordCheck.length > 32) {
      validationErrors.newPasswordCheck = "入力文字数は12文字から32文字以内です"
      hasError = true
    } else {
      validationErrors.newPasswordCheck = ""
    }

    // パスワードの一致チェック
    if (!hasError && formData.newPassword !== formData.newPasswordCheck) {
      validationErrors.newPasswordCheck = "パスワードが一致していません"
      hasError = true
    }

    setErrors(validationErrors)
    return !hasError
  }

  const convertEnabledValues = (data: any) => {
    return data
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      // バリデーション成功時の処理
      try {
        const convertedData = convertEnabledValues(formData)
        startLoading()
        await manageProfilePassword(convertedData.password, convertedData.newPassword)
        onClose(1)
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/login")
        } else if (error.response && error.response.status === 402) {
          //パスワードの形式誤り
          const errorDetail = error.response.data.detail
          const validationErrors = { ...errors }
          validationErrors.password = errorDetail.message
          validationErrors.newPassword = ""
          validationErrors.newPasswordCheck = ""
          setErrors(validationErrors)
        } else if (error.response) {
          //パスワード誤り
          const errorDetail = error.response.data.detail
          const validationErrors = { ...errors }
          validationErrors.password = ""
          validationErrors.newPassword = errorDetail.message
          validationErrors.newPasswordCheck = ""
          setErrors(validationErrors)
        } else {
          const validationErrors = { ...errors }
          validationErrors.password = ""
          validationErrors.newPassword = ""
          validationErrors.newPasswordCheck = "サーバーへの接続に失敗しました"
          setErrors(validationErrors)
        }
      } finally {
        stopLoading()
      }
    }
  }

  return (
    <div className="modal-bg-cover">
      <Card
        sx={{
          width: "90%",
          maxWidth: "500px",
          maxHeight: "calc(100vh - 100px)",
          marginTop: "50px",
          marginBottom: "50px",
          paddingTop: "20px",
          paddingBottom: "20px",
          paddingLeft: "20px"
        }}
      >
        <button className="close-button" onClick={() => onClose(0)}>
          ×
        </button>
        <MDTypography variant="h4" fontWeight="medium">
          パスワード変更
        </MDTypography>
        <MDBox style={{ height: "20px" }}></MDBox>
        <MDBox
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            paddingTop: "10px",
            paddingLeft: "5px",
            paddingRight: "25px"
          }}
        >
          <MDBox mb={2}>
            <MDInput
              type={showPassword.password ? "text" : "password"}
              label="Password"
              name="password"
              value={formData.password}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  "& fieldset": {
                    borderColor: "rgba(0, 0, 0, 0.1)"
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(0, 0, 0, 0.2)"
                  }
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleTogglePasswordVisibility("password")}
                      edge="end"
                      sx={{ color: "rgba(0, 0, 0, 0.54)" }}
                    >
                      {showPassword.password ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              onChange={(e) => {
                handleChange(e)
              }}
            />
          </MDBox>
          {errors.password && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.password}
            </MDBox>
          )}
          <MDBox mb={2}>
            <MDInput
              type={showPassword.newPassword ? "text" : "password"}
              label="New Password"
              name="newPassword"
              value={formData.newPassword}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  "& fieldset": {
                    borderColor: "rgba(0, 0, 0, 0.1)"
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(0, 0, 0, 0.2)"
                  }
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleTogglePasswordVisibility("newPassword")}
                      edge="end"
                      sx={{ color: "rgba(0, 0, 0, 0.54)" }}
                    >
                      {showPassword.newPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              onChange={(e) => {
                handleChange(e)
              }}
            />
          </MDBox>
          {errors.newPassword && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.newPassword}
            </MDBox>
          )}
          <MDBox mb={2}>
            <MDInput
              type={showPassword.newPasswordCheck ? "text" : "password"}
              label="New Password (Re Input)"
              name="newPasswordCheck"
              value={formData.newPasswordCheck}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  "& fieldset": {
                    borderColor: "rgba(0, 0, 0, 0.1)"
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(0, 0, 0, 0.2)"
                  }
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleTogglePasswordVisibility("newPasswordCheck")}
                      edge="end"
                      sx={{ color: "rgba(0, 0, 0, 0.54)" }}
                    >
                      {showPassword.newPasswordCheck ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              onChange={(e) => {
                handleChange(e)
              }}
            />
          </MDBox>
          {errors.newPasswordCheck && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.newPasswordCheck}
            </MDBox>
          )}
          <MDBox alignItems="center" mt={4} mb={2} sx={{ width: "100%", textAlign: "center" }}>
            <MDButton
              variant="contained"
              color="info"
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "1rem",
                padding: "0.75rem 0",
                backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1565c0"
                },
                width: "50%"
              }}
              onClick={(e) => {
                handleSubmit(e)
              }}
            >
              更新
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </div>
  )
}

export default ManagePassword
